/**
 * Victorian Sliders - Product Configurator Types
 * Admin interface for managing product configuration
 */

// ============================================
// WINDOW TYPES
// ============================================

export interface WindowType {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  basePrice: number;
  isActive: boolean;
  categories: string[];
  order: number;
}

// ============================================
// CATEGORIES
// ============================================

export enum CategoryType {
  STYLE = 'style',
  COLOR = 'color',
  HARDWARE = 'hardware',
  GLASS = 'glass',
  SIZE = 'size',
}

export const CategoryTypeLabels: Record<CategoryType, string> = {
  [CategoryType.STYLE]: 'Style',
  [CategoryType.COLOR]: 'Color',
  [CategoryType.HARDWARE]: 'Hardware',
  [CategoryType.GLASS]: 'Glass',
  [CategoryType.SIZE]: 'Size',
};

export interface Category {
  id: string;
  type: CategoryType;
  name: string;
  description?: string;
  isActive: boolean;
  options: CategoryOption[];
  order: number;
  // Configuration-driven fields
  responsePathPrefix?: string; // e.g., "window.measurements"
  questionGrouping?: 'individual' | 'grouped'; // How to present options
  showWhen?: Record<string, any>; // Conditional display logic
}

export type InputType =
  | 'text_input'
  | 'number_input'
  | 'boolean'
  | 'single_select'
  | 'multi_select'
  | 'single_select_with_image'
  | 'swatch_select'
  | 'visual_select'
  | 'color_picker';

export interface CategoryOption {
  id: string;
  name: string;
  description?: string;
  priceModifier: number; // Additional cost or percentage
  priceType: 'fixed' | 'percentage';
  imageUrl?: string;
  isActive: boolean;
  order: number;
  // Configuration-driven fields for question generation
  responsePath?: string; // Path in nested JSON (e.g., "window.measurements.heightMm")
  responseValue?: any; // Value to set when selected (for selects)
  inputType?: InputType; // Type of input widget
  validation?: {
    min?: number;
    max?: number;
    regex?: string;
    required?: boolean;
    notEqual?: string; // Path to compare against
  };
  showWhen?: Record<string, any>; // Conditional logic
  excludeFor?: string[]; // Product IDs to exclude from
  defaultValue?: any; // Default answer
}

// ============================================
// VICTORIAN SLIDERS BRANDING
// ============================================

export const VS_BRAND_COLORS = {
  primary: '#EE3A43', // Red
  secondary: '#000000', // Black
  background: '#FFFFFF',
  text: '#000000',
  textLight: '#666666',
  border: '#DDDDDD',
  hover: '#D12A33',
  success: '#28A745',
  warning: '#FFC107',
  error: '#DC3545',
};
